import React, {forwardRef, useContext, useImperativeHandle, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {UserHomepageControllerContext} from "../../../../../../index";
import ValidateMessages from "../../../../../../../../../core/models/static/texts/vallidate-messages";
import Form from "../../../../../../../../components/base/form";
import {Col} from "reactstrap";
import Button from "../../../../../../../../components/base/button/button";
import {UserHomepageComponentExpandability, UserHomepageComponentTypes} from "../../../../../../models";
import {MuiRFF, Yup} from '../../../../../../../../components/form';
import DateUtils from "../../../../../../../../../core/services/date-utils";

const UserHomepageControlPanelComponentDatesSection = forwardRef(({
                                                                      tabId,
                                                                      componentId,
                                                                      currentStartDate,
                                                                      currentEndDate,
                                                                      type,
                                                                      endDateRemovable = false,
                                                                      timeBased = false,
                                                                  }, ref) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.user-homepage.component-dates'});
    const controller = useContext(UserHomepageControllerContext);
    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();

    const initialState = useMemo(() => ({
        startDate: currentStartDate ? DateUtils.serverToUTC(currentStartDate) : undefined,
        endDate: currentEndDate ? DateUtils.serverToUTC(currentEndDate) : undefined,
    }), [currentStartDate, currentEndDate])


    const schema = useMemo(() => {
        return Yup.object().shape({
            startDate: Yup.mixed()
                .nullable()
                .validDate(ValidateMessages.incorrect),
            endDate: (function () {
                const schema = Yup.mixed()
                    .nullable()
                    .validDate(ValidateMessages.incorrect)
                    .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan('From-Date'), 'minute')
                    .disablePastDate(ValidateMessages.minDate('today'), 'minutes')
                if (endDateRemovable) {
                    return schema;
                }
                return schema.required(ValidateMessages.required)
            })(),
        })
    }, [endDateRemovable])

    const validate = MuiRFF.makeValidate(schema);
    const required = MuiRFF.makeRequired(schema);

    /**
     * With each change in the initialState and formRef of the component:
     * - provides the resetFields callback to the parent component.
     */
    useImperativeHandle(ref, () => ({
        resetFields: () => {
            formRef.current?.change('startDate', initialState.startDate);
            formRef.current?.resetFieldState('startDate');
            formRef.current?.change('endDate', initialState.endDate);
            formRef.current?.resetFieldState('endDate');
        }
    }), [initialState, formRef])

    /**
     * Updates the component dates.
     * @param {string} name
     * @param {dayjs.Dayjs | undefined} value
     */
    const updateComponentDates = (name, value) => {
        const serverValue = typeof value === 'undefined'
            ? value
            : DateUtils.UTCToServer(value).format(DateUtils.withoutOffsetFormat);
        const isTimeBasedEndDate = timeBased && name === 'endDate';
        const component = isTimeBasedEndDate
            ? {
                id: componentId,
                endDate: serverValue,
                content: {
                    finishDateTime: serverValue,
                },
            }
            : {
                id: componentId,
                [name]: serverValue,
            };
        controller.updateComponent(
            tabId,
            component,
            false,
            type?.id === UserHomepageComponentTypes.timeBased
                ? UserHomepageComponentExpandability.content
                : UserHomepageComponentExpandability.dates
        );
    }

    /**
     * Removes the value of the form that is associated with the given name.
     * @param {formApi} form
     * @param {string} name
     */
    const removeDate = (form, name) => {
        form.change(name, undefined);
        form.resetFieldState(name);
        updateComponentDates(name, undefined);
    }

    /**
     * Injects 23:59 time to the fully entered value of the date.
     *
     * * injection only happens if the user continues to enter the date and not remove something.
     * @param {formApi} form
     * @param {string} name
     * @param {dayjs.Dayjs} value
     */
    const injectTimeOnDateSubmission = (form, name, value) => {
        form.change(name, value);
        if (value.isValid()) {
            updateComponentDates(name, value);
        }
    }

    return (
        <>
            <div className={'component-dates-section'}>
                <p>
                    {timeBased ? t('timeBasedDescription') : t('description')}
                </p>
                <Form
                    className='w-100 d-flex flex-wrap justify-content-center'
                    onSubmit={() => false}
                    validate={validate}
                    initialValues={initialState}
                    render={({values, form}) => {
                        formRef.current = form;
                        return (
                            <>
                                <Col xs={12} className={'mb-3 px-0 d-flex flex-column align-items-center'}>
                                    <MuiRFF.DateTimePicker
                                        onChange={(value) => injectTimeOnDateSubmission(form, 'startDate', value)}
                                        textFieldProps={{
                                            onBlur: (e) => {
                                                try {
                                                    const date = DateUtils.localToUTC(DateUtils.local(e.target.value))
                                                    injectTimeOnDateSubmission(form, 'startDate', date);
                                                } catch {
                                                    // Ignore the error
                                                }
                                            }
                                        }}
                                        name={'startDate'}
                                        label={t('fromLabel')}
                                        required={required.startDate}
                                        maxDateTime={values.endDate}
                                    />
                                    {
                                        values?.startDate &&
                                        <Button
                                            className=' mt-1'
                                            variant={'text'}
                                            color={'error'}
                                            onClick={() => removeDate(form, 'startDate')}
                                        >
                                            Remove the start Date
                                        </Button>
                                    }
                                </Col>
                                <Col xs={12} className={'mb-3 px-0 d-flex flex-column align-items-center'}>
                                    <MuiRFF.DateTimePicker
                                        onChange={(value) => injectTimeOnDateSubmission(form, 'endDate', value)}
                                        textFieldProps={{
                                            onBlur: (e) => {
                                                try {
                                                    const date = DateUtils.localToUTC(DateUtils.local(e.target.value))
                                                    injectTimeOnDateSubmission(form, 'endDate', date);
                                                } catch {
                                                    // Ignore the error
                                                }
                                            }
                                        }}
                                        name={'endDate'}
                                        label={timeBased ? t('dealEndsLabel') : t('toLabel')}
                                        required={required.endDate}
                                        minDateTime={values.startDate}
                                    />
                                    {
                                        timeBased &&
                                        <p className={'text-sm text-color-2 mt-1 mb-0 text-center'}>
                                            {t('dealEndsHint')}
                                        </p>
                                    }
                                    {
                                        values?.endDate && endDateRemovable &&
                                        <Button
                                            className='mt-1'
                                            variant={'text'}
                                            color={'error'}
                                            onClick={() => removeDate(form, 'endDate')}
                                        >
                                            Remove the expiry Date
                                        </Button>
                                    }
                                </Col>
                            </>
                        );
                    }}
                />
            </div>
        </>
    );

})

export default UserHomepageControlPanelComponentDatesSection;
